import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaUser, 
  FaIdCard, 
  FaEnvelope, 
  FaPhone, 
  FaSignOutAlt,
  FaGasPump,
  FaBuilding,
  FaCalendarAlt,
  FaShieldAlt
} from 'react-icons/fa';
import { MdPerson, MdEmail, MdPhone, MdLogout, MdVerified } from 'react-icons/md';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import '../../styles/Perfil.css';

// Função para pegar iniciais do nome
function getIniciais(nome) {
  if (!nome) return 'FV';
  return nome
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

// Função para formatar CPF
function formatarCpf(cpf) {
  if (!cpf) return '';
  const numeros = cpf.replace(/\D/g, '');
  return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

// Modal de confirmação
const ConfirmDialog = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p>{message}</p>
        </div>
        <div className="modal-footer">
          <button className="modal-btn-cancel" onClick={onClose}>
            Cancelar
          </button>
          <button className="modal-btn-confirm" onClick={onConfirm}>
            Sair
          </button>
        </div>
      </div>
    </div>
  );
};

export default function PerfilScreen() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  async function handleSair() {
    setShowConfirmDialog(false);
    console.log('Perfil - Iniciando logout...');
    await logout();
    console.log('Perfil - Logout concluído, redirecionando...');
    setTimeout(() => {
      navigate('/login');
      console.log('Perfil - Redirecionamento executado');
    }, 100);
  }

  // Dados do usuário
  const userData = {
    nome: user?.nome || 'Frentista',
    cpf: user?.cpf || '',
    email: user?.email || 'frentista@guardvisio.com.br',
    telefone: user?.telefone || '(86) 99812-3456',
    posto: user?.posto?.nome || 'Posto não vinculado',
    endereco: user?.posto?.endereco || 'Endereço não informado',
    ativo: user?.posto?.ativo || true,
    dataCadastro: user?.data_cadastro || '01/01/2024'
  };

  return (
    <>
      <div className="perfil-web">
        {/* Header do perfil */}
        <div className="perfil-header-web">
          <div className="avatar-container">
            <div className="avatar-web">
              <span className="avatar-text-web">
                {getIniciais(userData.nome)}
              </span>
            </div>
            <div className="avatar-status verified">
              <MdVerified />
            </div>
          </div>
          <div className="header-info">
            <h1 className="user-name-web">{userData.nome}</h1>
            <p className="user-role-web">Frentista</p>
            <div className="user-badges">
              <span className="badge-web">
                <FaShieldAlt />
                Verificado
              </span>
            </div>
          </div>
        </div>

        {/* Grid de informações */}
        <div className="perfil-grid">
          {/* Dados Pessoais */}
          <div className="perfil-card">
            <div className="card-header-web">
              <FaUser className="card-icon" />
              <h3>Dados Pessoais</h3>
            </div>
            <div className="card-content">
              <div className="info-row-web">
                <div className="info-label">
                  <FaIdCard />
                  <span>CPF</span>
                </div>
                <div className="info-value">
                  {userData.cpf ? formatarCpf(userData.cpf) : 'Não informado'}
                </div>
              </div>
              <div className="info-row-web">
                <div className="info-label">
                  <FaCalendarAlt />
                  <span>Cadastro</span>
                </div>
                <div className="info-value">{userData.dataCadastro}</div>
              </div>
            </div>
          </div>

          {/* Contato */}
          <div className="perfil-card">
            <div className="card-header-web">
              <FaEnvelope className="card-icon" />
              <h3>Contato</h3>
            </div>
            <div className="card-content">
              <div className="info-row-web">
                <div className="info-label">
                  <FaEnvelope />
                  <span>E-mail</span>
                </div>
                <div className="info-value">{userData.email}</div>
              </div>
              <div className="info-row-web">
                <div className="info-label">
                  <FaPhone />
                  <span>Telefone</span>
                </div>
                <div className="info-value">{userData.telefone}</div>
              </div>
            </div>
          </div>

          {/* Posto de Trabalho */}
          <div className="perfil-card">
            <div className="card-header-web">
              <FaGasPump className="card-icon" />
              <h3>Posto de Trabalho</h3>
            </div>
            <div className="card-content">
              <div className="info-row-web">
                <div className="info-label">
                  <FaBuilding />
                  <span>Posto</span>
                </div>
                <div className="info-value">{userData.posto}</div>
              </div>
              <div className="info-row-web">
                <div className="info-label">
                  <HiOutlineLocationMarker />
                  <span>Endereço</span>
                </div>
                <div className="info-value">{userData.endereco}</div>
              </div>
              <div className="info-row-web">
                <div className="info-label">
                  <FaShieldAlt />
                  <span>Status</span>
                </div>
                <div className="info-value">
                  <span className={`status-badge ${userData.ativo ? 'active' : 'inactive'}`}>
                    {userData.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Botão Sair */}
        <div className="logout-section">
          <button 
            className="btn-logout-web"
            onClick={() => setShowConfirmDialog(true)}
          >
            <FaSignOutAlt />
            <span>Sair da conta</span>
          </button>
          <p className="app-version-web">Versão 1.0.0</p>
        </div>
      </div>

      {/* Modal de Confirmação */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleSair}
        title="Sair da conta"
        message="Tem certeza que deseja sair?"
      />
    </>
  );
}